// CreateSkillDialog — create a registry skill: name/slug/description/SKILL.md
// content. Externally controlled (isOpen/onClose), same convention as
// features/chat/share-thread/ui/ShareThreadDialog.tsx.

import { useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { validateSkillFrontmatter, type Skill } from '@/entities/skill';
import { deriveSlug } from '../model/deriveSlug';
import { useCreateSkill } from '../model/useCreateSkill';

function CreateSkillDialogContent({
  close,
  onCreated,
}: {
  close: () => void;
  onCreated?: (skill: Skill) => void;
}) {
  const { createSkill, isPending } = useCreateSkill();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [skillMdContent, setSkillMdContent] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleNameChange = (value: string) => {
    setName(value);
    if (!slugTouched) setSlug(deriveSlug(value));
  };

  const canSubmit =
    name.trim().length > 0 &&
    /^[a-z0-9][a-z0-9-]*$/.test(slug) &&
    skillMdContent.trim().length > 0 &&
    !isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const frontmatterError = validateSkillFrontmatter(skillMdContent);
    if (frontmatterError) {
      setError(frontmatterError);
      return;
    }
    setError(null);
    try {
      const skill = await createSkill({
        name: name.trim(),
        slug,
        description: description.trim(),
        skillMdContent,
      });
      onCreated?.(skill);
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create skill');
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Create skill</DialogTitle>
      </DialogHeader>

      <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto">
        {error && (
          <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="create-skill-name">Name</Label>
          <Input
            id="create-skill-name"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="e.g. Adobe Presentation Builder"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="create-skill-slug">Slug</Label>
          <Input
            id="create-skill-slug"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugTouched(true);
            }}
            aria-describedby="create-skill-slug-hint"
            placeholder="adobe-presentation-builder"
          />
          <p id="create-skill-slug-hint" className="text-xs text-muted-foreground">
            Lowercase letters, numbers, and hyphens only. Must be unique.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="create-skill-description">Description</Label>
          <Textarea
            id="create-skill-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="What does this skill help agents do?"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="create-skill-md">SKILL.md content</Label>
          <Textarea
            id="create-skill-md"
            value={skillMdContent}
            onChange={(e) => setSkillMdContent(e.target.value)}
            rows={10}
            className="font-mono text-xs"
            placeholder="# Skill name&#10;&#10;Instructions for the agent..."
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="secondary" onClick={close}>
          Cancel
        </Button>
        <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
          {isPending ? 'Creating…' : 'Create skill'}
        </Button>
      </DialogFooter>
    </>
  );
}

export function CreateSkillDialog({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (skill: Skill) => void;
}) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent aria-label="Create skill" className="sm:max-w-lg">
        {isOpen && <CreateSkillDialogContent close={onClose} onCreated={onCreated} />}
      </DialogContent>
    </Dialog>
  );
}
